// Pure gameplay definitions shared by the browser and authoritative module.

export const DayNight = {
  midnight: 0, dawn: 0.25, noon: 0.5, dusk: 0.75,

  wrap(t: number): number { return Number.isFinite(t) ? t - Math.floor(t) : 0; },

  /** The time of day at a world clock reading; phase is the time at clock 0, and a day length of 0 stops it there. */
  timeOf: (clockSeconds: number, phase: number, daySeconds: number) =>
    DayNight.wrap(daySeconds > 0 ? clockSeconds / daySeconds + phase : phase),

  /** The phase that makes timeOf give this time at this clock reading. */
  phaseFor: (time: number, clockSeconds: number, daySeconds: number) =>
    DayNight.wrap(daySeconds > 0 ? time - clockSeconds / daySeconds : time),

  /** "hh:mm" or a name (dawn, noon...); null if it isn't a time. */
  parse(text: string): number | null {
    const t = text.trim().toLowerCase();
    switch (t) {
      case 'midnight': return DayNight.midnight;
      case 'dawn': case 'sunrise': return DayNight.dawn;
      case 'morning': return 8 / 24;
      case 'day': case 'noon': return DayNight.noon;
      case 'dusk': case 'sunset': case 'evening': return DayNight.dusk;
      case 'night': return 22 / 24;
    }
    const parts = t.split(':');
    if (parts.length !== 2 || !/^\d+$/.test(parts[0]) || !/^\d{2}$/.test(parts[1])) return null;
    const h = Number(parts[0]), m = Number(parts[1]);
    return h > 23 || m > 59 ? null : (h * 60 + m) / (24 * 60);
  },

  /** "07:30". */
  format(time: number): string {
    const minutes = Math.floor(DayNight.wrap(time) * 24 * 60) % (24 * 60);
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  },
};

/** The time of day as last told by the world, run on by the session's clock. */
export class DayClock {
  private time = DayNight.noon;
  private daySeconds = 0;
  private at = 0;

  /** The world's word: the time now (`now`: the session's clock, seconds) and how long a day lasts (0 = stopped). */
  set(time: number, daySeconds: number, now: number): void {
    this.time = DayNight.wrap(Number.isFinite(time) ? time : DayNight.noon);
    this.daySeconds = Math.max(0, daySeconds);
    this.at = now;
  }

  timeAt(now: number): number {
    return this.daySeconds > 0 ? DayNight.wrap(this.time + (now - this.at) / this.daySeconds) : this.time;
  }
}
